import { useState, useRef, useEffect, useCallback } from 'react'

const WS_BASE = import.meta.env.VITE_WS_BASE || 'wss://verifylink-api.ryanhexrivk.workers.dev'

export default function useSignaling({ roomCode, onMessage }) {
  const [connected, setConnected] = useState(false)
  const wsRef = useRef(null)
  const reconnectTimerRef = useRef(null)
  const isConnectingRef = useRef(false)
  const shouldReconnectRef = useRef(true)

  const connect = useCallback(() => {
    if (!roomCode) return
    if (isConnectingRef.current) {
      console.log('[Signaling] Already connecting, skipping')
      return
    }

    // Clean up any existing connection or timer
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    if (wsRef.current) {
      try {
        wsRef.current.close()
      } catch (e) {
        // ignore
      }
      wsRef.current = null
    }

    isConnectingRef.current = true
    console.log('[Signaling] Connecting to room:', roomCode)

    try {
      const ws = new WebSocket(`${WS_BASE}/api/pair/${roomCode}`)
      wsRef.current = ws

      ws.onopen = () => {
        console.log('[Signaling] WebSocket opened')
        isConnectingRef.current = false
        setConnected(true)
      }

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data)
          console.log('[Signaling] Message received:', msg.type)
          onMessage?.(msg)
        } catch (err) {
          console.warn('[Signaling] Non-JSON message:', event.data)
        }
      }

      ws.onclose = (event) => {
        console.log('[Signaling] WebSocket closed:', event.code, event.reason)
        isConnectingRef.current = false
        setConnected(false)
        wsRef.current = null
        if (shouldReconnectRef.current) {
          reconnectTimerRef.current = setTimeout(() => {
            console.log('[Signaling] Attempting reconnect...')
            connect()
          }, 3000)
        }
      }

      ws.onerror = (error) => {
        console.error('[Signaling] WebSocket error:', error)
        isConnectingRef.current = false
        setConnected(false)
      }
    } catch (err) {
      console.error('[Signaling] Connection error:', err)
      isConnectingRef.current = false
      setConnected(false)
      if (shouldReconnectRef.current) {
        reconnectTimerRef.current = setTimeout(() => {
          connect()
        }, 3000)
      }
    }
  }, [roomCode, onMessage])

  const send = useCallback((data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data))
    } else {
      console.warn('[Signaling] Cannot send, WebSocket not open')
    }
  }, [])

  const disconnect = useCallback(() => {
    console.log('[Signaling] Disconnecting')
    shouldReconnectRef.current = false
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    if (wsRef.current) {
      wsRef.current.close(1000)
      wsRef.current = null
    }
    setConnected(false)
  }, [])

  useEffect(() => {
    return () => {
      shouldReconnectRef.current = false
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
      }
      if (wsRef.current) {
        wsRef.current.close(1000)
      }
    }
  }, [])

  return { connected, connect, send, disconnect }
}
